import { createClient } from "@supabase/supabase-js";

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  }
);

// ============================================
// 건물 데이터 타입 정의
// ============================================

export interface BuildingStructureBox {
  position: [number, number, number];
  scale: [number, number, number];
}

export interface BuildingData {
  id: string;
  position_x: number;
  position_y: number;
  position_z: number;
  mesh_index: number;
  building_structure?: BuildingStructureBox[] | null;
  building_text?: string | null;
  title?: string | null;
  message1?: string | null;
  message2?: string | null;
  user_id: string;
  created_at?: string;
  updated_at?: string;
  // user_profiles 조인 결과
  user_profiles?: {
    user_color: string;
    username: string;
  } | null;
}

export interface InsertBuildingData {
  position_x: number;
  position_y: number;
  position_z: number;
  mesh_index: number;
  building_structure?: BuildingStructureBox[] | null;
  building_text?: string | null;
  title?: string | null;
  message1?: string | null;
  message2?: string | null;
  user_id: string;
}

// ============================================
// 화환 데이터 타입 정의
// ============================================

export interface WreathData {
  id: string;
  building_id: string;
  message?: string | null;
  user_id: string;
  final_position_x?: number | null;
  final_position_y?: number | null;
  final_position_z?: number | null;
  dropped_at?: string;
  created_at?: string;
}

export interface InsertWreathData {
  building_id: string;
  message?: string | null;
  user_id: string;
  final_position_x?: number | null;
  final_position_y?: number | null;
  final_position_z?: number | null;
}

// ============================================
// 실시간 커서 데이터 타입
// ============================================

export interface CursorData {
  id: string;
  user_id: string;
  grid_x: number;
  grid_z: number;
  color: string;
  updated_at: string;
}

// ============================================
// 공용 스위치 상태 타입
// ============================================

export interface GlobalSwitchData {
  id: string;
  is_light_mode: boolean;
  updated_at: string;
}

// ============================================
// 사용자 프로필 데이터 타입
// ============================================

export interface UserProfileData {
  id: string;
  username: string;
  password: string;
  user_color: string;
  created_at?: string;
  updated_at?: string;
}

export interface InsertUserProfileData {
  username: string;
  password: string;
  user_color?: string;
}

export interface UpdateUserProfileData {
  password?: string;
  user_color?: string;
}

// ============================================
// 입주자 데이터 타입 정의
// ============================================

export type ProjectionAxis = "x" | "-x" | "z" | "-z";

export interface TenantData {
  id: string;
  building_id: string;
  user_id: string;
  text: string;
  projection_axis: ProjectionAxis;
  position_u: number; // 0.0 ~ 1.0 (가로 비율)
  position_v: number; // 0.0 ~ 1.0 (세로 비율)
  created_at?: string;
  updated_at?: string;
}

export interface InsertTenantData {
  building_id: string;
  user_id: string;
  text: string;
  projection_axis: ProjectionAxis;
  position_u: number;
  position_v: number;
}

// ============================================
// 장식 데이터 타입 정의
// ============================================

export type DecorationType = "spline_background" | "restricted_zone" | "prop";

// 스플라인 배경 데이터 (강, 호수, 공원 등 - 확장 가능)
export interface SplineBackgroundData {
  subType: "river" | "lake" | "park" | string; // 확장 가능한 서브타입
  controlPoints: Array<{ x: number; z: number }>; // 스플라인 컨트롤 포인트
  width: number; // 너비
  color?: string; // 색상
  flowDirection?: 1 | -1; // 흐름 방향 (강용)
  flowSpeed?: number; // 흐름 속도
}

// 제한 영역 데이터
export interface RestrictedZoneData {
  cells: Array<{ x: number; z: number }>; // 제한할 셀 목록
  reason?: string; // 제한 사유
  visualStyle?: "striped" | "dotted" | "solid"; // 시각적 스타일
}

// 2D Prop 데이터 (SVG 기반)
export interface PropData {
  propType: "sign" | "streetlight" | "tree" | "bench" | string; // 확장 가능
  position: { x: number; y: number; z: number }; // 자유 좌표
  rotation?: number; // Y축 회전 (도)
  scale?: number; // 크기 배율
  customText?: string; // 표지판 텍스트 등
}

// 통합 장식 데이터
export interface DecorationData {
  id: string;
  decoration_type: DecorationType;
  data: SplineBackgroundData | RestrictedZoneData | PropData;
  affected_cells: Array<{ x: number; z: number }>;
  created_by: string;
  created_at?: string;
  updated_at?: string;
}

export interface InsertDecorationData {
  decoration_type: DecorationType;
  data: SplineBackgroundData | RestrictedZoneData | PropData;
  affected_cells: Array<{ x: number; z: number }>;
  created_by: string;
}
